import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { DollarSign, Target, TrendingUp, Percent, Building2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { PeriodSelector, KPICard, IndicatorRow, DepartmentBadge } from "@/components/UIComponents";
import { TrendModal } from "@/components/TrendModal";
import { RevenuDetailModal } from "@/components/RevenuDetailModal";
import { formatCurrency, formatPercent, MONTHS, CHART_COLORS, CONFIG } from "@/lib/config";
import api from "@/lib/api";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="font-medium text-slate-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={entry.id || entry._id || index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="money-value">{formatCurrency(entry.value)}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const AnalyseRevenus = () => {
  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState(1);
  const [quarter, setQuarter] = useState(1);
  const [view, setView] = useState("mensuel");
  const [selectedDept, setSelectedDept] = useState("GLOBAL");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [ratios, setRatios] = useState(null);
  
  // Modal de tendance
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);
  
  // Modal détail revenus
  const [showRevenuDetail, setShowRevenuDetail] = useState(false);
  const [selectedDeptForRevenu, setSelectedDeptForRevenu] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { getMonthsForPeriod, aggregateMonthlyData } = await import('@/lib/periodUtils');
        const periods = getMonthsForPeriod(view, year, month, quarter);
        
        const monthsData = await Promise.all(
          periods.map(p => api.getDashboard(p.year, p.month))
        );
        
        const dashboard = aggregateMonthlyData(monthsData);
        setDashboardData(dashboard);
        
        if (view === "mensuel") {
          const ratiosData = await api.getRatios(year, month);
          setRatios(ratiosData);
        }
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year, month, quarter, view]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  const kpis = dashboardData?.kpis || {};
  const globalKpi = kpis.GLOBAL || {};
  const taxes = ratios?.taxes || {};

  // Données pour le graphique par département
  const deptChartData = CONFIG.departments.map(dept => ({
    departement: CONFIG.department_names[dept],
    bancaire: kpis[dept]?.revenus?.bancaire || 0,
    cash: kpis[dept]?.revenus?.cash || 0
  }));

  return (
    <div data-testid="revenus-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Analyse des Revenus</h1>
          <p className="text-slate-500 mt-1 text-sm">{MONTHS[month]} {year}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-full sm:w-48 bg-white">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GLOBAL">📊 Tous départements</SelectItem>
              <SelectItem value="PRQC">🎨 {CONFIG.department_names.PRQC}</SelectItem>
              <SelectItem value="LMDS">🏪 {CONFIG.department_names.LMDS}</SelectItem>
              <SelectItem value="LOCATION">🚚 {CONFIG.department_names.LOCATION}</SelectItem>
            </SelectContent>
          </Select>
          <PeriodSelector 
            year={year} 
            setYear={setYear} 
            month={month} 
            setMonth={setMonth}
            view={view}
            setView={setView}
            quarter={quarter}
            setQuarter={setQuarter}
          />
        </div>
      </div>

      {/* KPIs Revenus */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="CA Total"
          value={formatCurrency(globalKpi.revenus?.total)}
          subtitle="Bancaire + Cash"
          icon={DollarSign}
          color="emerald"
          kpiKey="revenus_total"
          onClick={() => {
            setSelectedKPI({title: "CA Total", kpiKey: "revenus_total", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="CA Bancaire"
          value={formatCurrency(globalKpi.revenus?.bancaire)}
          subtitle="Transactions légales"
          icon={DollarSign}
          color="blue"
          kpiKey="revenus_bancaire"
          onClick={() => {
            setSelectedKPI({title: "CA Bancaire", kpiKey: "revenus_bancaire", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="CA Cash"
          value={formatCurrency(globalKpi.revenus?.cash)}
          subtitle="Sans taxes"
          icon={DollarSign}
          color="purple"
          kpiKey="revenus_cash"
          onClick={() => {
            setSelectedKPI({title: "CA Cash", kpiKey: "revenus_cash", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="% Objectif atteint"
          value={formatPercent(globalKpi.revenus?.pct_objectif)}
          subtitle={`Objectif: ${formatCurrency(globalKpi.revenus?.objectif)}`}
          icon={Target}
          color={globalKpi.revenus?.pct_objectif >= 100 ? "emerald" : "amber"}
          kpiKey="revenus_objectif"
          onClick={() => {
            setSelectedKPI({title: "% Objectif atteint", kpiKey: "revenus_objectif", isPercent: true, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
      </div>

      {/* Graphique + Tableau */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in delay-200">
        {/* Graphique revenus par département */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Revenus par département</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deptChartData}>
                  <XAxis 
                    dataKey="departement" 
                    tick={{ fontSize: 9 }} 
                    angle={-25}
                    textAnchor="end"
                    height={70}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v/1000}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                  <Bar dataKey="bancaire" name="Bancaire" fill={CHART_COLORS.revenus} stackId="a" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="cash" name="Cash" fill={CHART_COLORS.profit} stackId="a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Détails par département */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Détail par département</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {CONFIG.departments.map(dept => {
              const deptKpi = kpis[dept] || {};
              const total = deptKpi.revenus?.total || 0;
              const globalTotal = globalKpi.revenus?.total || 1;
              const pctGlobal = (total / globalTotal * 100);
              
              return (
                <div 
                  key={dept} 
                  className="border-b border-slate-100 pb-3 last:border-0 hover:bg-green-50 cursor-pointer transition-colors rounded-lg p-3 -m-3"
                  onClick={() => {
                    setSelectedDeptForRevenu(dept);
                    setShowRevenuDetail(true);
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <DepartmentBadge dept={dept} />
                    <span className="font-heading font-bold text-lg text-slate-900">
                      {formatCurrency(total)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                    <span>Bancaire: {formatCurrency(deptKpi.revenus?.bancaire)}</span>
                    <span>•</span>
                    <span>Cash: {formatCurrency(deptKpi.revenus?.cash)}</span>
                    {deptKpi.revenus?.autres > 0 && (
                      <>
                        <span>•</span>
                        <span>Intérêts: {formatCurrency(deptKpi.revenus?.autres)}</span>
                      </>
                    )}
                  </div>
                  <Progress value={pctGlobal} className="h-2" />
                  <p className="text-xs text-slate-400 mt-1">
                    {formatPercent(pctGlobal)} du CA total • Cliquez pour voir les transactions
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Taxes */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Analyse des Taxes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-emerald-50 rounded-lg p-4">
              <p className="kpi-label text-emerald-600 mb-1">TPS Perçue</p>
              <p className="kpi-value text-xl text-emerald-700">{formatCurrency(taxes.percues_tps)}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4">
              <p className="kpi-label text-emerald-600 mb-1">TVQ Perçue</p>
              <p className="kpi-value text-xl text-emerald-700">{formatCurrency(taxes.percues_tvq)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="kpi-label text-red-600 mb-1">TPS Payée</p>
              <p className="kpi-value text-xl text-red-700">{formatCurrency(taxes.payees_tps)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="kpi-label text-red-600 mb-1">TVQ Payée</p>
              <p className="kpi-value text-xl text-red-700">{formatCurrency(taxes.payees_tvq)}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <p className="kpi-label text-amber-600 mb-1">Solde Net à Remettre</p>
              <p className="kpi-value text-xl text-amber-700">{formatCurrency(taxes.solde_net)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal Tendance */}
      {showTrendModal && selectedKPI && (
        <TrendModal
          open={showTrendModal}
          onClose={() => setShowTrendModal(false)}
          title={selectedKPI.title}
          kpiKey={selectedKPI.kpiKey}
          departement={selectedKPI.departement}
          isPercent={selectedKPI.isPercent}
          chartType={selectedKPI.isPercent ? "line" : "bar"}
        />
      )}
      
      {/* Modal Détail Revenus */}
      {showRevenuDetail && selectedDeptForRevenu && (
        <RevenuDetailModal
          open={showRevenuDetail}
          onClose={() => {
            setShowRevenuDetail(false);
            setSelectedDeptForRevenu(null);
          }}
          departement={selectedDeptForRevenu}
          year={year}
          month={month}
        />
      )}
    </div>
  );
};

export default AnalyseRevenus;
